import { heightAt as worldHeightAt, inIsland, ISLAND_MIN, ISLAND_SIZE } from '../world/Heightmap';

/** 1 unit = 1 m. Grid fallback for DS-S-007 / DS-F-012 — Recast is not required. */
export const NAV_MODE = 'grid' as const;
export const NAV_TILE_SIZE = 32;
export const NAV_CELL_SIZE = 2;
export const NAV_CELLS = NAV_TILE_SIZE / NAV_CELL_SIZE;
export const NAV_TILE_MIN = ISLAND_MIN / NAV_TILE_SIZE;
export const NAV_TILE_MAX = NAV_TILE_MIN + ISLAND_SIZE / NAV_TILE_SIZE - 1;
/** |height| at or below this is "near 0" and walkable. */
export const NAV_WALK_HEIGHT = 0.5;
export const NAV_MAX_SLOPE_DEG = 50;
export const NAV_WINDOW_RADIUS = 1;

export type NavMode = typeof NAV_MODE | 'recast';
export type NavXz = { x: number; z: number };
export type NavPoint = { x: number; y: number; z: number };
export type NavSample = { walkable: boolean; height: number; swim: boolean };
export type NavBlocker = { minX: number; maxX: number; minZ: number; maxZ: number };
export type NavHeightFn = (x: number, z: number) => number;
export type NavWaterFn = (x: number, z: number) => boolean;

export type NavGridOptions = {
  height?: NavHeightFn;
  water?: NavWaterFn;
  blockers?: readonly NavBlocker[];
};

/** Cheap Crash Coast wreck AABBs (cockpit / tail / cabin). */
export const WRECK_BLOCKERS: readonly NavBlocker[] = [
  { minX: -0.6, maxX: 1.3, minZ: 10.1, maxZ: 14.2 },
  { minX: -0.75, maxX: 0.75, minZ: -13.7, maxZ: -10.3 },
  { minX: -10.1, maxX: -5.9, minZ: -25.2, maxZ: -18.8 },
];

/** Foam line at z≈16. Off-mesh water — findPath returns null. */
export function isCoastWater(_x: number, z: number): boolean {
  return z >= 16;
}

export function tileIndex(v: number): number {
  return Math.floor(v / NAV_TILE_SIZE);
}

export function worldToTile(x: number, z: number): { ix: number; iz: number } {
  return { ix: tileIndex(x), iz: tileIndex(z) };
}

export function tileKey(ix: number, iz: number): string {
  return `${ix},${iz}`;
}

export function parseTileKey(key: string): { ix: number; iz: number } {
  const sep = key.indexOf(',');
  const ix = Number(key.slice(0, sep));
  const iz = Number(key.slice(sep + 1));
  if (!Number.isInteger(ix) || !Number.isInteger(iz)) {
    throw new Error(`bad nav tile key: ${key}`);
  }
  return { ix, iz };
}

export function tileOrigin(ix: number, iz: number): { x: number; z: number } {
  return { x: ix * NAV_TILE_SIZE, z: iz * NAV_TILE_SIZE };
}

export function inIslandTile(ix: number, iz: number): boolean {
  return ix >= NAV_TILE_MIN && ix <= NAV_TILE_MAX && iz >= NAV_TILE_MIN && iz <= NAV_TILE_MAX;
}

export function clampTileIndex(c: number): number {
  if (c < NAV_TILE_MIN) {
    return NAV_TILE_MIN;
  }
  if (c > NAV_TILE_MAX) {
    return NAV_TILE_MAX;
  }
  return c;
}

export function cellIndex(v: number): number {
  return Math.floor(v / NAV_CELL_SIZE);
}

export function cellCenter(c: number): number {
  return c * NAV_CELL_SIZE + NAV_CELL_SIZE * 0.5;
}

export function worldToCell(x: number, z: number): { gx: number; gz: number } {
  return { gx: cellIndex(x), gz: cellIndex(z) };
}

/** 3×3 (radius 1) around the player. Off-island tiles are dropped. */
export function residentNavKeysAt(x: number, z: number, radius = NAV_WINDOW_RADIUS): string[] {
  const ix0 = clampTileIndex(tileIndex(x));
  const iz0 = clampTileIndex(tileIndex(z));
  const keys: string[] = [];
  for (let dz = -radius; dz <= radius; dz++) {
    for (let dx = -radius; dx <= radius; dx++) {
      const ix = ix0 + dx;
      const iz = iz0 + dz;
      if (inIslandTile(ix, iz)) {
        keys.push(tileKey(ix, iz));
      }
    }
  }
  return keys;
}

const SQRT2 = Math.SQRT2;
const OCTILE = SQRT2 - 2;
const MAX_EXPAND = 4096;
const NEAREST_RINGS = 16;
const STEP_SAMPLES = 8;

const CARDINAL: readonly [number, number, number][] = [
  [1, 0, 1],
  [-1, 0, 1],
  [0, 1, 1],
  [0, -1, 1],
];
const DIAGONAL: readonly [number, number, number][] = [
  [1, 1, SQRT2],
  [1, -1, SQRT2],
  [-1, 1, SQRT2],
  [-1, -1, SQRT2],
];

type AStarNode = {
  gx: number;
  gz: number;
  g: number;
  f: number;
  px: number;
  pz: number;
};

/**
 * 32 m tiles / 2 m cells. Walkable when height is near 0, slope < 50°,
 * and the cell is not inside a static blocker AABB.
 */
export class NavGrid {
  readonly mode: NavMode = NAV_MODE;
  private readonly tiles = new Set<string>();
  private readonly blockers: NavBlocker[];
  private readonly heightFn: NavHeightFn;
  private readonly waterFn: NavWaterFn | null;

  constructor(opts: NavGridOptions = {}) {
    this.heightFn = opts.height ?? worldHeightAt;
    this.waterFn = opts.water ?? null;
    this.blockers = opts.blockers ? [...opts.blockers] : [];
  }

  get residentCount(): number {
    return this.tiles.size;
  }

  residentKeys(): string[] {
    return [...this.tiles];
  }

  addBlocker(box: NavBlocker): void {
    this.blockers.push(box);
  }

  loadTile(ix: number, iz: number): void {
    if (!inIslandTile(ix, iz)) {
      return;
    }
    this.tiles.add(tileKey(ix, iz));
  }

  unloadTile(ix: number, iz: number): void {
    this.tiles.delete(tileKey(ix, iz));
  }

  /** Keep the same 3×3 window shape as the streamer, at nav-tile resolution. */
  syncWindow(x: number, z: number, radius = NAV_WINDOW_RADIUS): void {
    if (!Number.isFinite(x) || !Number.isFinite(z)) {
      return;
    }
    const wanted = new Set(residentNavKeysAt(x, z, radius));
    for (const key of this.tiles) {
      if (!wanted.has(key)) {
        this.tiles.delete(key);
      }
    }
    for (const key of wanted) {
      const { ix, iz } = parseTileKey(key);
      this.loadTile(ix, iz);
    }
  }

  sample(x: number, z: number): NavSample {
    if (!Number.isFinite(x) || !Number.isFinite(z) || !inIsland(x, z)) {
      return { walkable: false, height: 0, swim: false };
    }
    const height = this.heightFn(x, z);
    const swim = this.waterFn ? this.waterFn(x, z) : false;
    if (swim) {
      return { walkable: false, height, swim: true };
    }
    if (this.pointBlocked(x, z)) {
      return { walkable: false, height, swim: false };
    }
    if (Math.abs(height) > NAV_WALK_HEIGHT) {
      return { walkable: false, height, swim: false };
    }
    if (this.slopeDeg(x, z) > NAV_MAX_SLOPE_DEG) {
      return { walkable: false, height, swim: false };
    }
    return { walkable: true, height, swim: false };
  }

  nearestWalkable(x: number, z: number): NavPoint | null {
    if (this.sample(x, z).walkable) {
      return this.point(x, z);
    }
    const origin = worldToCell(x, z);
    for (let r = 1; r <= NEAREST_RINGS; r++) {
      for (let gz = origin.gz - r; gz <= origin.gz + r; gz++) {
        for (let gx = origin.gx - r; gx <= origin.gx + r; gx++) {
          if (Math.abs(gx - origin.gx) !== r && Math.abs(gz - origin.gz) !== r) {
            continue;
          }
          const wx = cellCenter(gx);
          const wz = cellCenter(gz);
          if (this.cellWalkable(gx, gz)) {
            return this.point(wx, wz);
          }
        }
      }
    }
    return null;
  }

  /**
   * A* across loaded tiles (corridor auto-loaded). Water dest → null.
   * Open ground returns cell-center waypoints plus the exact ends.
   */
  findPath(from: NavXz, to: NavXz): NavPoint[] | null {
    if (!Number.isFinite(from.x) || !Number.isFinite(from.z) || !Number.isFinite(to.x) || !Number.isFinite(to.z)) {
      return null;
    }
    if (this.sample(to.x, to.z).swim) {
      return null;
    }
    this.ensureCorridor(from, to);
    const start = this.nearestWalkable(from.x, from.z);
    const goal = this.sample(to.x, to.z).walkable ? this.point(to.x, to.z) : this.nearestWalkable(to.x, to.z);
    if (!start || !goal) {
      return null;
    }
    if (start.x === goal.x && start.z === goal.z) {
      return [start, goal];
    }
    const sg = worldToCell(start.x, start.z);
    const gg = worldToCell(goal.x, goal.z);
    if (sg.gx === gg.gx && sg.gz === gg.gz) {
      return [start, goal];
    }
    const cells = this.astar(sg, gg);
    if (!cells) {
      return null;
    }
    const path: NavPoint[] = [start];
    for (let i = 1; i < cells.length - 1; i++) {
      const cell = cells[i];
      if (!cell) {
        continue;
      }
      path.push(this.point(cellCenter(cell.gx), cellCenter(cell.gz)));
    }
    path.push(goal);
    return path;
  }

  /** One greedy cell (or clipped point) toward `to`. Safe no-op on open ground. */
  step(from: NavXz, to: NavXz): NavPoint {
    if (this.sample(to.x, to.z).walkable) {
      return this.point(to.x, to.z);
    }
    if (this.sample(to.x, to.z).swim) {
      return this.clipToLand(from, to);
    }
    const here = worldToCell(from.x, from.z);
    let best: NavPoint | null = null;
    let bestD = Infinity;
    for (const [dx, dz] of [...CARDINAL, ...DIAGONAL]) {
      const gx = here.gx + dx;
      const gz = here.gz + dz;
      if (dx !== 0 && dz !== 0 && (!this.cellWalkable(here.gx + dx, here.gz) || !this.cellWalkable(here.gx, here.gz + dz))) {
        continue;
      }
      if (!this.cellWalkable(gx, gz)) {
        continue;
      }
      const wx = cellCenter(gx);
      const wz = cellCenter(gz);
      const d = (wx - to.x) * (wx - to.x) + (wz - to.z) * (wz - to.z);
      if (d < bestD) {
        bestD = d;
        best = this.point(wx, wz);
      }
    }
    return best ?? this.nearestWalkable(from.x, from.z) ?? this.point(from.x, from.z);
  }

  private clipToLand(from: NavXz, to: NavXz): NavPoint {
    let x = from.x;
    let z = from.z;
    for (let i = 1; i <= STEP_SAMPLES; i++) {
      const t = i / STEP_SAMPLES;
      const nx = from.x + (to.x - from.x) * t;
      const nz = from.z + (to.z - from.z) * t;
      if (!this.sample(nx, nz).walkable) {
        break;
      }
      x = nx;
      z = nz;
    }
    return this.point(x, z);
  }

  private ensureCorridor(from: NavXz, to: NavXz): void {
    const minX = Math.min(from.x, to.x);
    const maxX = Math.max(from.x, to.x);
    const minZ = Math.min(from.z, to.z);
    const maxZ = Math.max(from.z, to.z);
    const ix0 = tileIndex(minX);
    const ix1 = tileIndex(maxX);
    const iz0 = tileIndex(minZ);
    const iz1 = tileIndex(maxZ);
    for (let iz = iz0; iz <= iz1; iz++) {
      for (let ix = ix0; ix <= ix1; ix++) {
        this.loadTile(ix, iz);
      }
    }
  }

  private astar(start: { gx: number; gz: number }, goal: { gx: number; gz: number }): { gx: number; gz: number }[] | null {
    const startKey = cellKey(start.gx, start.gz);
    const goalKey = cellKey(goal.gx, goal.gz);
    const nodes = new Map<string, AStarNode>();
    const open: string[] = [startKey];
    const closed = new Set<string>();
    nodes.set(startKey, {
      gx: start.gx,
      gz: start.gz,
      g: 0,
      f: octile(start.gx, start.gz, goal.gx, goal.gz),
      px: start.gx,
      pz: start.gz,
    });

    let expands = 0;
    while (open.length > 0 && expands < MAX_EXPAND) {
      expands += 1;
      const currentKey = popMin(open, nodes);
      if (!currentKey) {
        break;
      }
      if (currentKey === goalKey) {
        return reconstruct(nodes, currentKey);
      }
      closed.add(currentKey);
      const cur = nodes.get(currentKey);
      if (!cur) {
        continue;
      }
      this.expand(cur, goal, nodes, open, closed);
    }
    return null;
  }

  private expand(
    cur: AStarNode,
    goal: { gx: number; gz: number },
    nodes: Map<string, AStarNode>,
    open: string[],
    closed: Set<string>,
  ): void {
    for (const [dx, dz, cost] of CARDINAL) {
      this.relax(cur, cur.gx + dx, cur.gz + dz, cost, goal, nodes, open, closed);
    }
    for (const [dx, dz, cost] of DIAGONAL) {
      if (!this.cellWalkable(cur.gx + dx, cur.gz) || !this.cellWalkable(cur.gx, cur.gz + dz)) {
        continue;
      }
      this.relax(cur, cur.gx + dx, cur.gz + dz, cost, goal, nodes, open, closed);
    }
  }

  private relax(
    cur: AStarNode,
    gx: number,
    gz: number,
    cost: number,
    goal: { gx: number; gz: number },
    nodes: Map<string, AStarNode>,
    open: string[],
    closed: Set<string>,
  ): void {
    const key = cellKey(gx, gz);
    if (closed.has(key) || !this.cellWalkable(gx, gz)) {
      return;
    }
    const g = cur.g + cost;
    const prev = nodes.get(key);
    if (prev && g >= prev.g) {
      return;
    }
    const next: AStarNode = {
      gx,
      gz,
      g,
      f: g + octile(gx, gz, goal.gx, goal.gz),
      px: cur.gx,
      pz: cur.gz,
    };
    nodes.set(key, next);
    if (!open.includes(key)) {
      open.push(key);
    }
  }

  private cellWalkable(gx: number, gz: number): boolean {
    const x = cellCenter(gx);
    const z = cellCenter(gz);
    if (!inIsland(x, z)) {
      return false;
    }
    if (this.waterFn?.(x, z)) {
      return false;
    }
    if (this.cellBlocked(gx, gz)) {
      return false;
    }
    const height = this.heightFn(x, z);
    if (Math.abs(height) > NAV_WALK_HEIGHT) {
      return false;
    }
    return this.slopeDeg(x, z) <= NAV_MAX_SLOPE_DEG;
  }

  private pointBlocked(x: number, z: number): boolean {
    for (const box of this.blockers) {
      if (x >= box.minX && x <= box.maxX && z >= box.minZ && z <= box.maxZ) {
        return true;
      }
    }
    return false;
  }

  private cellBlocked(gx: number, gz: number): boolean {
    const minX = gx * NAV_CELL_SIZE;
    const maxX = minX + NAV_CELL_SIZE;
    const minZ = gz * NAV_CELL_SIZE;
    const maxZ = minZ + NAV_CELL_SIZE;
    for (const box of this.blockers) {
      if (minX < box.maxX && maxX > box.minX && minZ < box.maxZ && maxZ > box.minZ) {
        return true;
      }
    }
    return false;
  }

  private slopeDeg(x: number, z: number): number {
    const h = this.heightFn;
    const step = NAV_CELL_SIZE;
    const dx = (h(x + step, z) - h(x - step, z)) / (2 * step);
    const dz = (h(x, z + step) - h(x, z - step)) / (2 * step);
    return (Math.atan(Math.hypot(dx, dz)) * 180) / Math.PI;
  }

  private point(x: number, z: number): NavPoint {
    return { x, y: this.heightFn(x, z), z };
  }
}

function cellKey(gx: number, gz: number): string {
  return `${gx},${gz}`;
}

function octile(ax: number, az: number, bx: number, bz: number): number {
  const dx = Math.abs(ax - bx);
  const dz = Math.abs(az - bz);
  return dx + dz + OCTILE * Math.min(dx, dz);
}

function popMin(open: string[], nodes: Map<string, AStarNode>): string | null {
  let bestI = -1;
  let bestF = Infinity;
  for (let i = 0; i < open.length; i++) {
    const key = open[i];
    if (key === undefined) {
      continue;
    }
    const node = nodes.get(key);
    if (node && node.f < bestF) {
      bestF = node.f;
      bestI = i;
    }
  }
  if (bestI < 0) {
    return null;
  }
  const picked = open.splice(bestI, 1)[0];
  return picked ?? null;
}

function reconstruct(nodes: Map<string, AStarNode>, endKey: string): { gx: number; gz: number }[] {
  const out: { gx: number; gz: number }[] = [];
  let key: string | null = endKey;
  const seen = new Set<string>();
  while (key && !seen.has(key)) {
    seen.add(key);
    const node = nodes.get(key);
    if (!node) {
      break;
    }
    out.push({ gx: node.gx, gz: node.gz });
    if (node.px === node.gx && node.pz === node.gz) {
      break;
    }
    key = cellKey(node.px, node.pz);
  }
  out.reverse();
  return out;
}
